import React, { Fragment, useState } from "react";
import { Redirect } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";
import Cookies from "js-cookie";

import RedirectErrorModal from "../../../AuthenticatedApp/components/modals/RedirectErrorModal";
import { Portal } from "../../../AuthenticatedApp/components/modals/Portal";
import NonAuthLayout from "../../components/header/NonAuthHeaderLayout";
import UrlCorrectionBox from "../../components/UrlCorrectionBox";
import { StyledPersonalAuthentication } from "../../../../styles/PersonalAuthentication";
import { ReactComponent as SmsPhone } from "../../../../assets/icons/PhoneSms.svg";
import { validateLoginToken } from "../../../../redux/actions/signin.actions";
import { sendLoginValidationToken } from "../../../../redux/actions/signin.actions";
import { convertDateToYMD, extractDateProperties } from "../../../../utils/utilityFunctions";
import * as eventLogger from "../../../../eventLogger";
import { LOGIN, LOGIN_FAILED } from "../../../../eventLogger/segment/constants/eventTags";
import {
    LOGIN_METHOD,
    FAILURE_REASON,
} from "../../../../eventLogger/segment/constants/eventProperties";

import { SmsValidationContent, ValidateLoginContainer } from "./styles";
import ConfirmKudaAccount from "../../components/ConfirmKudaAccount";
import PhoneValidationOptions from "../../layout/signin/PhoneValidationOptions";
import { useAuditLog } from "../../../../utils/_hooks/useAuditLog";
import { getIDFromEnum, kudaBusinessTierEnum } from "../../../../utils/allEnums";
import SuccessModal from "../../../AuthenticatedApp/components/modals/SuccessModal";

const processLoginResponse = (response) => {
    const { message, userID, tokens, status, kudaBusinessTier, smeProfileID, accountNumber, data } =
        response;

    const completelyOnboarded =
        kudaBusinessTier === getIDFromEnum("CompletelyOnboarded", kudaBusinessTierEnum) &&
        data.groups.length !== 0;

    if (status) {
        if (tokens === undefined) {
            return { showRedirectTokenModal: true };
        }

        const sessionData = {
            completelyOnboarded,
            userID,
            tokens,
            email: data.email,
            smeProfileID,
            accountNumber,
        };

        sessionStorage.setItem("kudaAppToken", tokens.accessToken);
        if (completelyOnboarded) {
            sessionStorage.setItem("completelyOnboarded", completelyOnboarded);
            sessionStorage.setItem("kudaUserID", userID);
        } else {
            Cookies.set("kudaUserID", userID, { secure: true, path: "/" });
            Cookies.set("kudaUserEmail", data.email, { secure: true, path: "/" });
            Cookies.set("kudaSmeID", smeProfileID, { secure: true, path: "/" });
            Cookies.set("kudaAccountNumber", accountNumber, { secure: true, path: "/" });
        }

        Cookies.set("kudaAppRefreshToken", tokens.refreshToken, { secure: true, path: "/" });
        Cookies.set("expiresIn", tokens.tokenExpiryInMinutes, { secure: true, path: "/" });

        return { status: true, sessionData, data };
    } else {
        return { status: false, message };
    }
};

const ValidateLogin = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const auditLog = useAuditLog();

    const { user, fromLogin } = useSelector((state) => state.signInState);
    const { customerDebitCards } = useSelector((state) => state.cardsState);

    const [errMsg, setErrMsg] = useState("");
    const [pin, setPin] = useState("");
    const [page, setPage] = useState("selection-page");
    const email = sessionStorage.getItem("kudaUserEmail");

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showRedirectTokenModal, setShowRedirectTokenModal] = useState(false);
    const [sendValidationLoading, setSendValidationLoading] = useState(false);
    const [showSendValidationError, setShowSendValidationError] = useState(false);
    const [validationErrMsg, setValidationErrMsg] = useState("");
    const [showTokenValidationError, setShowTokenValidationError] = useState(false);
    const [validateOtpState, setValidateOtpState] = useState(false);
    const [validationPhoneNumber, setValidationPhoneNumber] = useState("");
    const [validationType, setValidationType] = useState();

    const handleInputPinChange = (value) => {
        setPin(value);
    };

    const sendValidationMsg = async (data) => {
        setSendValidationLoading(true);
        setShowSendValidationError(false);
        const auditLogData = {
            ...auditLog,
            moduleName: "login",
            action:
                data === 2
                    ? "sendLoginPhoneOTPValidation"
                    : data === 1
                    ? "sendLoginAppValidation"
                    : data === 3
                    ? "sendLoginEmailOTPValidation"
                    : "",
            description:
                data === 2
                    ? "Send Phone Otp to Customer during login "
                    : data === 1
                    ? "Send App Validation Token during login"
                    : data === 3
                    ? "Send Email Otp to Customer during login "
                    : "",
        };
        const payload = {
            notificationType: data,
            auditLogData,
        };

        const { status, message, number } = await dispatch(sendLoginValidationToken(payload));

        if (status) {
            const valueToSend = data === 2 ? number?.slice(-2) : number;
            setValidationPhoneNumber(valueToSend);
            setValidationType(data);

            setPage("sms-page");

            setSendValidationLoading(false);
        } else {
            setTimeout(() => {
                setSendValidationLoading(false);
                setValidationErrMsg(message);
                setShowSendValidationError(true);
            }, 3000);
        }
    };

    const handleTokenConfirm = async () => {
        setValidateOtpState(true);
        const auditLogData = {
            ...auditLog,
            moduleName: "login",
            action: "validateLoginToken",
            description: "Validate Token for Login",
        };

        const payload = {
            token: pin,
            isNomineeInvite: false,
            notificationType: validationType,
            auditLogData,
        };

        const response = await dispatch(validateLoginToken(payload));

        const processResponse = processLoginResponse(response);
        setValidateOtpState(false);

        if (processResponse.showRedirectTokenModal) {
            setShowRedirectTokenModal(true);
            return;
        }

        if (processResponse.status) {
            const { sessionData, data } = processResponse;

            setErrMsg("");
            setShowSuccessModal(true);

            /**-----------------SEGMENT---------------------------- */
            const segmentData = {
                [LOGIN_METHOD]: "email_login",
            };

            const result = data.otherNames.trim().split(/\s+/);
            const firstName = result[0];
            const middleName = result[1];

            const { year, month, day } = extractDateProperties(data.creationDate);

            const userData = {
                email: user?.email ? user?.email : email,
                name: `${data.lastname} ${data.otherNames}`,
                first_name: firstName,
                middle_name: middleName,
                last_name: data.lastname,
                gender: data.gender === 1 ? "Male" : "Female",
                date_of_birth: convertDateToYMD(data.dateOfBirth),
                birthday: convertDateToYMD(data.dateOfBirth),
                phone: data.phoneNumber,
                address: data.address,
                // customer_type: "Existing K4B Customer",
                total_accounts: data.groups.length,
                freelance_accounts: data.groups?.filter(
                    (account) =>
                        account?.businessProfile.businessCategory !== null &&
                        account?.businessProfile.businessCategory === 3,
                ).length,
                full_business_accounts: data.groups?.filter(
                    (account) =>
                        account?.businessProfile.businessCategory !== null &&
                        account?.businessProfile.businessCategory !== 3,
                ).length,
                anniversary_date: data.creationDate,
                anniversary_day_string: day,
                anniversary_month_string: month,
                anniversary_year_string: year,
                has_card: customerDebitCards > 0 ? true : false
            };

            eventLogger.pushEventGlobally(LOGIN, segmentData);
            eventLogger.identifyUser(sessionData.userID, userData);
            /**---------------------------------------------------------------- */
        } else {
            setErrMsg(
                processResponse.message === "Token validation failed"
                    ? "OTP validation failed"
                    : "Sorry an error occurred",
            );
            setShowTokenValidationError(true);

            const segmentData = {
                [LOGIN_METHOD]: "email_login",
                [FAILURE_REASON]: processResponse.message,
            };
            eventLogger.pushEventGlobally(LOGIN_FAILED, segmentData);
        }
    };

    const handleSuccessModalClose = () => {
        setShowSuccessModal(false);
        history.push({
            pathname: "/create-passkey",
            state: { redirectPath: history.location?.state?.redirectPath },
        });
    };

    if (!fromLogin) {
        return <Redirect to="/login" />;
    }

    return (
        <Fragment>
            <NonAuthLayout />

            {page === "selection-page" && (
                <PhoneValidationOptions
                    {...{
                        title: "Validate This Sign In",
                        subtitle: "Sign in securely with one of the options below.",
                        sendValidationLoading,
                        showSendValidationError,
                        sendValidationMsg,
                        validationErrMsg,
                    }}
                />
            )}

            {page === "sms-page" && (
                <ConfirmKudaAccount
                    pin={pin}
                    handleInputPinChange={handleInputPinChange}
                    showTokenValidationError={showTokenValidationError}
                    validationPhoneNumber={validationPhoneNumber}
                    handleTokenConfirm={handleTokenConfirm}
                    validateOtpState={validateOtpState}
                    errMsg={errMsg}
                    validationType={validationType}
                    dispatchAction={sendLoginValidationToken}
                />
            )}

            {page === "app-page" && (
                <ValidateLoginContainer>
                    <div className="kuda-auth--layout">
                        <div className="auth-container">
                            <div className="flex align-start justify-between auth-layout-wrap">
                                <div className="kuda-auth--wrap">
                                    <UrlCorrectionBox />

                                    <div className="kuda-login--wrap">
                                        <StyledPersonalAuthentication>
                                            <SmsValidationContent>
                                                <div className="mb-3">
                                                    <SmsPhone />
                                                </div>
                                                <div className="mb-3">
                                                    <h5 className="color-black signup-text text-subheading-md">
                                                        We’re waiting for your response
                                                    </h5>
                                                </div>
                                                <div className="sub-heading-text">
                                                    Approve this sign in by opening your Kuda app
                                                    and tapping
                                                    <span className="bold-txt"> Yes it’s me.</span>
                                                </div>

                                                <div className="infinite-path width-wide">
                                                    <span id="elem" className="shape trail"></span>
                                                </div>
                                                <div className="approval-txt">
                                                    {" "}
                                                    Haven’t received an approval request?{" "}
                                                    <span className="bold-txt">click here.</span>
                                                </div>
                                            </SmsValidationContent>
                                        </StyledPersonalAuthentication>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </ValidateLoginContainer>
            )}

            {showSuccessModal && (
                <Portal>
                    <SuccessModal
                        message="Sign In Confirmed"
                        btnText="Continue"
                        doSomethingElse={handleSuccessModalClose}
                    >
                        <div className="password-set--message">
                            <p className="text-center">
                                A few more steps and you’ll be back in your account.
                            </p>
                        </div>
                    </SuccessModal>
                </Portal>
            )}

            {showRedirectTokenModal && (
                <Portal>
                    <RedirectErrorModal message="Sorry, an error occurred" url="/login" />
                </Portal>
            )}
        </Fragment>
    );
};

export { processLoginResponse };
export default ValidateLogin;
